import { createClient, unwrap, type TokenStore } from '@openroad/api-client';
import { QueryClient } from '@tanstack/react-query';

/**
 * Where tokens live.
 *
 * The access token stays in memory only — it is the one an XSS would most like
 * to read, and it expires in fifteen minutes anyway. The refresh token goes to
 * `localStorage` so a reload does not sign the member out.
 *
 * That is a real trade, not an oversight: `localStorage` is readable by any
 * script on the origin. The alternative — an httpOnly cookie — does not work
 * for the Capacitor and Tauri shells, which is the whole reason the API is
 * token-based (ADR-0008). Revisit if the web app ever stops sharing a build
 * with them.
 */
const REFRESH_TOKEN_KEY = 'openroad.refresh';

class BrowserTokenStore implements TokenStore {
  private accessToken: string | null = null;

  getAccessToken() {
    return this.accessToken;
  }

  getRefreshToken() {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      // Private browsing modes can throw rather than return null.
      return null;
    }
  }

  setTokens(tokens: { accessToken: string; refreshToken: string } | null) {
    this.accessToken = tokens?.accessToken ?? null;
    try {
      if (tokens) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      else localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // A session that cannot be persisted still works until the tab closes.
    }
  }
}

export const tokenStore = new BrowserTokenStore();

/**
 * Where the API is, for this build.
 *
 * On the web the app and the API share an origin — Vite proxies `/api` in
 * development, Caddy serves both in production — so a path is enough, and a
 * path is the default because it hardcodes no host anywhere.
 *
 * The Android and desktop shells load the identical bundle from a WebView
 * origin of their own: `https://localhost` under Capacitor, `tauri://localhost`
 * under Tauri. There `/api` is the shell's own asset server and not the API at
 * all, so those builds are given an absolute URL through `VITE_API_BASE_URL`.
 * Their build scripts refuse to run without one, because an APK that cannot
 * reach a server is worse than one that was never built.
 *
 * A malformed value throws here rather than turning every request into a
 * confusing 404. It is a build-time constant, so this fails identically on the
 * first page load of every build — which makes it a build error in practice.
 */
export function resolveApiBaseUrl(configured: string | undefined): string {
  const value = configured?.trim();
  if (!value) return '/api';

  const withoutTrailingSlash = value.replace(/\/+$/, '');

  // A path on this origin.
  if (withoutTrailingSlash.startsWith('/') && !withoutTrailingSlash.startsWith('//')) {
    return withoutTrailingSlash;
  }

  // Or an absolute http(s) URL. Anything else — a bare `example.com/api`, a
  // protocol-relative `//host`, a `javascript:` — resolves against the shell's
  // own origin or worse, so it is refused by name.
  let parsed: URL;
  try {
    parsed = new URL(withoutTrailingSlash);
  } catch {
    throw new Error(
      `VITE_API_BASE_URL must be a path such as /api or an absolute http(s) URL, got: ${value}`,
    );
  }

  const problem = apiBaseUrlProblem(parsed);
  if (problem) throw new Error(`VITE_API_BASE_URL ${problem}, got: ${value}`);

  return withoutTrailingSlash;
}

/**
 * Why a parsed URL cannot be an API base, or `null` if it can.
 *
 * Split out because the shell builds have to make the same judgement before
 * spending three minutes on a Gradle run, and two implementations of "is this
 * a usable base URL" would eventually disagree — the build accepting what the
 * installed app then refuses is the worst of the two orders.
 *
 * Every rejection here is a value that parses and then does not work:
 * `https://` has no host, a `?query` is dropped the moment a path is appended
 * to it, a `#fragment` is never sent, and credentials in a URL are a secret in
 * a build artefact.
 */
export function apiBaseUrlProblem(url: URL): string | null {
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return `must be http or https, not ${url.protocol}`;
  }
  if (url.hostname === '') return 'has no host';
  if (url.username !== '' || url.password !== '') return 'must not carry credentials';
  if (url.search !== '') return 'must not carry a query string';
  if (url.hash !== '') return 'must not carry a fragment';
  return null;
}

export const apiBaseUrl = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

export const api = createClient({ baseUrl: apiBaseUrl, tokens: tokenStore });

export { unwrap };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Ride availability changes; a stale list that says a seat is free when
      // it is not wastes somebody's tap. Thirty seconds is short enough to be
      // honest and long enough to avoid refetching on every navigation.
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry what will fail the same way again.
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

/** Money from the API is `{ cents, currency }`; this is how it reaches a screen. */
export function formatMoney(money: { cents: number; currency: string }): string {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: money.currency,
    // The design writes "$32", never "$32.00".
    minimumFractionDigits: money.cents % 100 === 0 ? 0 : 2,
  }).format(money.cents / 100);
}

/** "7:30am" — the form every time in the design takes. */
export function formatTime(iso: string): string {
  return new Intl.DateTimeFormat('en-NZ', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Pacific/Auckland',
  })
    .format(new Date(iso))
    .replace(/\s/g, '')
    .toLowerCase();
}

/**
 * "Fri 14 Feb" — the form the design writes a date in.
 *
 * Assembled from the parts rather than taken from `format()`, which puts a
 * comma after the weekday in en-NZ. The order is the design's, not the
 * locale's, so it does not move if the locale data changes.
 */
export function formatDay(iso: string): string {
  const parts = new Map(
    new Intl.DateTimeFormat('en-NZ', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'Pacific/Auckland',
    })
      .formatToParts(new Date(iso))
      .map((part) => [part.type, part.value]),
  );

  return `${parts.get('weekday')} ${parts.get('day')} ${parts.get('month')}`;
}

/**
 * "2h 45" — hours and minutes, as the ride card shows a duration.
 *
 * Rounded to minutes first, then split. Taking the hours before rounding the
 * remainder lets the minutes reach sixty and be printed there: a 3,599-second
 * leg is 59.98 minutes, which rounded on its own gives "0h 60".
 */
export function formatDuration(seconds: number): string {
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, '0')}`;
}

/** "14 kg". */
export function formatCo2(grams: number): string {
  return `${Math.round(grams / 1000)} kg`;
}

/** `YYYY-MM-DD` for an instant, as the calendar day it falls on in New Zealand. */
export function newZealandDateOf(instant: Date | string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(typeof instant === 'string' ? new Date(instant) : instant);
}

/** `YYYY-MM-DD` for today in New Zealand, which is what the search API wants. */
export function todayInNewZealand(): string {
  return newZealandDateOf(new Date());
}

interface ClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const HOUR_MS = 60 * 60 * 1000;

/** An instant, broken into the numbers Auckland's clock would be showing. */
function nzParts(instant: Date): ClockParts {
  const read: Record<string, number> = {};
  for (const part of new Intl.DateTimeFormat('en-US', {
    timeZone: 'Pacific/Auckland',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)) {
    if (part.type !== 'literal') read[part.type] = Number(part.value);
  }

  // A missing part means Intl answered something this code does not understand.
  // NaN carries that outward; zero would quietly become a real date.
  return {
    year: read.year ?? NaN,
    month: read.month ?? NaN,
    day: read.day ?? NaN,
    // Intl renders midnight as hour 24 in some engines.
    hour: (read.hour ?? NaN) % 24,
    minute: read.minute ?? NaN,
    second: read.second ?? NaN,
  };
}

/** How far ahead of UTC New Zealand is at a given instant, in milliseconds. */
function nzOffsetMs(instant: Date): number {
  const { year, month, day, hour, minute, second } = nzParts(instant);
  return Date.UTC(year, month - 1, day, hour, minute, second) - instant.getTime();
}

/**
 * A `datetime-local` value read as New Zealand wall time, returned as an ISO
 * instant.
 *
 * Every time this app prints is `Pacific/Auckland` — `formatTime`, `formatDay`
 * and `todayInNewZealand` all say so. Reading the publish form's departure in
 * the *browser's* zone instead broke that agreement in the one place it is
 * written rather than read: a driver typed 8:30 and the ride they had just
 * published said 6:30pm, because the machine was in Europe.
 *
 * Two passes, the same as the server's `zonedTimeToUtc`, because the offset
 * itself depends on the instant: NZST is +12 and NZDT is +13.
 *
 * Then two more looks, which the server deliberately does not take. Twice a
 * year a wall-clock time is not a moment:
 *
 * - **The morning the clocks go forward**, 2:30am never happens. Two passes
 *   resolve it to 3:30 — the convention `Temporal`'s `compatible`
 *   disambiguation uses, and the right one for a *search* window.
 * - **The morning they go back**, 2:30am happens twice, an hour apart, and two
 *   passes pick one of them without saying which.
 *
 * Both are the wrong answer for a departure somebody typed and will be held to:
 * the ride gets published an hour away from the time they chose and nothing
 * says so. A search window can absorb an hour; a person standing at a bus stop
 * cannot. So both are refused here, and the driver picks again.
 */
export function newZealandTimeToUtc(local: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!match) throw new Error(`"${local}" is not a datetime-local value`);

  const [, rawYear = '', rawMonth = '', rawDay = '', rawHour = '', rawMinute = ''] = match;
  const year = Number(rawYear);
  const month = Number(rawMonth);
  const day = Number(rawDay);
  const hour = Number(rawHour);
  const minute = Number(rawMinute);

  const naive = Date.UTC(year, month - 1, day, hour, minute);

  const firstPass = naive - nzOffsetMs(new Date(naive));
  const instant = new Date(naive - nzOffsetMs(new Date(firstPass)));

  const wanted = { year, month, day, hour, minute };
  const shows = (candidate: Date) => {
    const parts = nzParts(candidate);
    return (
      parts.year === wanted.year &&
      parts.month === wanted.month &&
      parts.day === wanted.day &&
      parts.hour === wanted.hour &&
      parts.minute === wanted.minute
    );
  };

  if (!shows(instant)) {
    throw new Error(
      'That time does not exist on that date in New Zealand — the clocks go forward an hour that morning. Please choose another time.',
    );
  }

  // An hour either side, because a New Zealand transition is exactly an hour.
  // If a neighbour shows the same wall time, the clock read it twice and there
  // is no way to know which one the driver meant.
  if (
    shows(new Date(instant.getTime() - HOUR_MS)) ||
    shows(new Date(instant.getTime() + HOUR_MS))
  ) {
    throw new Error(
      'That time happens twice on that date in New Zealand — the clocks go back an hour that morning. Please choose another time.',
    );
  }

  return instant.toISOString();
}
